import React, { FC, useRef, useEffect, useState } from 'react';
import { Card, Image, Flex, Box } from 'rebass/styled-components';
import { Input } from '@rebass/forms/styled-components';
import { variant } from 'util/constants';
import { Icon } from './icon';
import useProjects from 'hooks/useProjects';
import FileDropZone from './fileDropzone';
import { Spinner } from './spinner';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { setProject } from 'store/dataSlice';
import useFeature from 'hooks/useFeature';
import { ui, statusColor, stripButton } from 'util/ui';
import { TickIcon, CrossIcon, DuplicateIcon, DeleteIcon, ImageIcon } from './shell/Icons';
import { SUGGEST } from './FieldSuggestions';
import moment from 'moment';

const fieldStyle = (size: number, weight: number, color: string): React.CSSProperties => ({
  font: 'inherit',
  fontSize: size,
  fontWeight: weight,
  color,
  border: '1px solid transparent',
  borderRadius: 6,
  padding: '3px 6px',
  background: 'transparent',
  width: '100%',
  boxSizing: 'border-box',
  minWidth: 0,
});

type Props = {
  project: IProject;
  scale: number;
  setProjectsLength?: () => void;
};

const ProjectContainer: FC<Props> = ({ project, scale, setProjectsLength }: Props) => {
  const dispatch = useDispatch();
  const { removeProject, updateProject, duplicateProject } = useProjects();
  const [title, setTitle] = useState(project.title);
  const [jobNumber, setJobNumber] = useState(project.jobNumber);
  const [client, setClient] = useState(project.client);
  const [imageLoading, setImageLoading] = useState(false);
  const { uploadFeature } = useFeature(project.id);

  const onBlur = () => {
    if (title !== project.title || jobNumber !== project.jobNumber || client !== project.client) {
      updateProject(project.id, { title, jobNumber, client });
    }
  };

  useEffect(() => {
    if (project.title !== title) {
      setTitle(project.title);
    }
    if (project.jobNumber !== jobNumber) {
      setJobNumber(project.jobNumber);
    }
    if (project.client !== client) {
      setClient(project.client);
    }
  }, [project]);

  const upload = async (file: File) => {
    uploadFeature(file, (task) => {
      task.on(
        'state_changed',
        (snapshot) => {
          setImageLoading(true);
        },
        (error) => {
          setImageLoading(false);
        },
        () => {
          setImageLoading(false);
        },
      );
    });
  };

  const confirmDuplicateProject = (project: IProject) => {
    if(window.confirm('Are you sure you want to duplicate this project?')) {
      duplicateProject(project);
    }
  }

  const goToProject = () => {
    dispatch(setProject(project));
    if (setProjectsLength) {
      setProjectsLength();
    }
    sessionStorage.setItem('lastClickedProjectId', project.id);
    dispatch(push(`/project/${project.id}`));
  };

  // UI refresh card: status = frame colour + tick/cross (no words), quick actions on the strip,
  // still image (dropped onto to change, as before), and the text edits in place.
  const color = statusColor(!!project.complete);
  const toggleLabel = project.complete ? 'Complete. Click to mark incomplete' : 'Incomplete. Click to mark complete';
  const date = project.date ? moment.unix(Number(project.date)).format('D MMM YYYY') : '';
  return (
    <article
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: ui.surface,
        border: `2px solid ${color}`,
        borderRadius: ui.radius,
        overflow: 'hidden',
        color: ui.ink,
        height: '100%',
        fontFamily: ui.font,
      }}
    >
      <div style={{ background: color, display: 'flex', alignItems: 'center', gap: 4, padding: '5px 6px' }}>
        <span style={{ flex: 1 }} />
        {/* Status, duplicate and delete together on the right, as on the project page */}
        <button
          type="button"
          className="archy-iconbtn"
          aria-label={toggleLabel}
          title={toggleLabel}
          style={stripButton}
          onClick={() => updateProject(project.id, { complete: !project.complete })}
        >
          {project.complete ? <TickIcon /> : <CrossIcon />}
        </button>
        <button
          type="button"
          className="archy-iconbtn"
          aria-label="Duplicate project"
          title="Duplicate"
          style={stripButton}
          onClick={() => confirmDuplicateProject(project)}
        >
          <DuplicateIcon />
        </button>
        <button
          type="button"
          className="archy-iconbtn"
          aria-label="Delete project"
          title="Delete"
          style={stripButton}
          onClick={() => removeProject(project.id)}
        >
          <DeleteIcon />
        </button>
      </div>
      <Flex
        width={'100%'}
        overflow={'hidden'}
        justifyContent={'center'}
        alignItems={'center'}
        sx={{
          position: 'relative',
          aspectRatio: '16 / 9',
          height: 169 * scale,
          bg: '#24506B',
          cursor: 'pointer',
          ':active': { filter: 'brightness(0.8)' },
        }}
        onClick={goToProject}
        role="link"
        aria-label={`Open ${title || 'project'}`}
        title="Open project (drop an image here to change it)"
      >
        <FileDropZone upload={upload}>
          {imageLoading && (
            <Flex sx={{ position: 'absolute', width: '100%', height: '100%' }} justifyContent={'center'} alignItems={'center'}>
              <Spinner />
            </Flex>
          )}
          {project.featureURL ? (
            <Box
              opacity={imageLoading ? 0.25 : 1}
              height={'100%'}
              width={'100%'}
              sx={{
                ':hover': { filter: 'brightness(1.08)' },
                backgroundImage: `url('${project.featureURL}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          ) : (
            <Flex height="100%" width="100%" justifyContent="center" alignItems="center">
              <ImageIcon />
            </Flex>
          )}
        </FileDropZone>
      </Flex>
      <form onBlur={onBlur} style={{ padding: '10px 10px 12px', display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        <input
          className="archy-edit"
          aria-label="Project title"
          placeholder="Add project title"
          value={title}
          list={SUGGEST.title}
          autoComplete="off"
          onChange={(e) => setTitle(e.target.value)}
          style={fieldStyle(15, 600, ui.ink)}
        />
        <input
          className="archy-edit"
          aria-label="Client"
          placeholder="Add client"
          value={client}
          list={SUGGEST.client}
          autoComplete="off"
          onChange={(e) => setClient(e.target.value)}
          style={fieldStyle(13, 400, ui.muted)}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            className="archy-edit"
            aria-label="Project number"
            placeholder="Project number"
            value={jobNumber}
            list={SUGGEST.jobNumber}
            autoComplete="off"
            onChange={(e) => setJobNumber(e.target.value)}
            style={{ ...fieldStyle(12, 400, ui.muted), width: 110, flex: 'none' }}
          />
          <span style={{ fontSize: 12, color: ui.muted, whiteSpace: 'nowrap' }}>{date}</span>
        </div>
        {Array.isArray(project.tags) && project.tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 'auto', padding: '6px 6px 0' }}>
            {project.tags.slice(0, 4).map((t) => (
              <span key={t} style={{ fontSize: 11, color: ui.text2, background: ui.subtle, padding: '3px 8px', borderRadius: 6 }}>
                {t}
              </span>
            ))}
            {project.tags.length > 4 && <span style={{ fontSize: 11, color: ui.muted, padding: '3px 2px' }}>+{project.tags.length - 4}</span>}
          </div>
        )}
      </form>
    </article>
  );
};

export default ProjectContainer;
